/**
 * Environment Configuration Utility
 * Provides type-safe access to environment variables and environment-specific settings
 */

export interface EnvironmentConfig {
  // Environment Information
  env: string;
  envName: string;
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;

  // Supabase Configuration
  supabase: {
    url: string;
    anonKey: string;
    schema: string;
  };

  // External API Configuration
  externalApi: {
    baseUrl: string;
    token: string;
  };

  // Audio Upload Configuration
  audioUpload: {
    url: string;
  };

  // Section Upload Configuration
  sectionUpload: {
    url: string;
  };

  // Gateway API Configuration
  gateway: {
    baseUrl: string;
  };

  // Deepgram Configuration (Public Transcription page)
  deepgram: {
    apiKey: string;
  };

  // Google Cloud Text-to-Speech (Chirp HD voices) — used by the SPEAK
  // voice command's history read-back.
  googleTts: {
    apiKey: string;
  };

  // Feature Flags
  features: {
    debugLogging: boolean;
    mockData: boolean;
    cloudFunctionDemo: boolean;
    integrationTesting: boolean;
    performanceMonitoring: boolean;
    analytics: boolean;
    errorReporting: boolean;
    cache: boolean;
    compression: boolean;
  };

  // Server Configuration
  server: {
    port: number;
    host: string;
  };

  // API Configuration
  api: {
    timeout: number;
  };
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, fallback: string = ''): string {
  return import.meta.env[key] || fallback;
}

/**
 * Get boolean environment variable
 */
function getBoolEnvVar(key: string, fallback: boolean = false): boolean {
  const value = getEnvVar(key);
  return value === 'true' || value === '1' || fallback;
}

/**
 * Get number environment variable
 */
function getNumberEnvVar(key: string, fallback: number): number {
  const value = getEnvVar(key);
  const parsed = parseInt(value);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Environment configuration object
 */
export const envConfig: EnvironmentConfig = {
  // Environment Information
  env: getEnvVar('VITE_ENV', 'development'),
  envName: getEnvVar('VITE_ENV_NAME', 'Development'),
  isDevelopment: getEnvVar('VITE_ENV') === 'development',
  isStaging: getEnvVar('VITE_ENV') === 'staging',
  isProduction: getEnvVar('VITE_ENV') === 'production',

  // Supabase Configuration
  supabase: {
    url: getEnvVar('VITE_SUPABASE_URL'),
    anonKey: getEnvVar('VITE_SUPABASE_ANON_KEY'),
    schema: getEnvVar('VITE_SUPABASE_SCHEMA', 'public'),
  },

  // External API Configuration
  externalApi: {
    baseUrl: getEnvVar('VITE_EXTERNAL_API_BASE_URL'),
    token: getEnvVar('VITE_EXTERNAL_API_TOKEN'),
  },

  // Audio Upload Configuration
  audioUpload: {
    url: getEnvVar('VITE_N8N_AUDIO_UPLOAD_URL'),
  },

  // Section Upload Configuration
  sectionUpload: {
    url: getEnvVar('VITE_N8N_SECTION_UPLOAD_URL'),
  },

  // Gateway API Configuration
  gateway: {
    baseUrl: getEnvVar('VITE_GATEWAY_API_BASE_URL'),
  },

  // Deepgram Configuration (Public Transcription page)
  deepgram: {
    apiKey: getEnvVar('VITE_DEEPGRAM_API_KEY'),
  },

  // Google Cloud Text-to-Speech (Chirp HD voices)
  googleTts: {
    apiKey: getEnvVar('VITE_GOOGLE_TTS_API_KEY'),
  },

  // Feature Flags
  features: {
    debugLogging: getBoolEnvVar('VITE_ENABLE_DEBUG_LOGGING', false),
    mockData: getBoolEnvVar('VITE_ENABLE_MOCK_DATA', false),
    cloudFunctionDemo: getBoolEnvVar('VITE_ENABLE_CLOUD_FUNCTION_DEMO', false),
    integrationTesting: getBoolEnvVar('VITE_ENABLE_INTEGRATION_TESTING', false),
    performanceMonitoring: getBoolEnvVar(
      'VITE_ENABLE_PERFORMANCE_MONITORING',
      false
    ),
    analytics: getBoolEnvVar('VITE_ENABLE_ANALYTICS', false),
    errorReporting: getBoolEnvVar('VITE_ENABLE_ERROR_REPORTING', false),
    cache: getBoolEnvVar('VITE_ENABLE_CACHE', false),
    compression: getBoolEnvVar('VITE_ENABLE_COMPRESSION', false),
  },

  // Server Configuration
  server: {
    port: getNumberEnvVar('VITE_DEV_SERVER_PORT', 8081),
    host: getEnvVar('VITE_DEV_SERVER_HOST', 'localhost'),
  },

  // API Configuration
  api: {
    timeout: getNumberEnvVar('VITE_API_TIMEOUT', 30000),
  },
};

/**
 * Validate required environment variables
 */
export function validateEnvironment(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check required Supabase configuration
  if (!envConfig.supabase.url) {
    errors.push('VITE_SUPABASE_URL is required');
  }
  if (!envConfig.supabase.anonKey) {
    errors.push('VITE_SUPABASE_ANON_KEY is required');
  }

  // Check required External API configuration
  if (!envConfig.externalApi.baseUrl) {
    errors.push('VITE_EXTERNAL_API_BASE_URL is required');
  }
  if (!envConfig.externalApi.token) {
    errors.push('VITE_EXTERNAL_API_TOKEN is required');
  }

  // Check required Audio Upload configuration
  if (!envConfig.audioUpload.url) {
    errors.push('VITE_N8N_AUDIO_UPLOAD_URL is required');
  }

  // Check required Section Upload configuration
  if (!envConfig.sectionUpload.url) {
    errors.push('VITE_N8N_SECTION_UPLOAD_URL is required');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get environment-specific configuration
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  return envConfig;
}

/**
 * Log environment information (development only)
 */
export function logEnvironmentInfo(): void {
  if (envConfig.isDevelopment && envConfig.features.debugLogging) {
    console.group('🌍 Environment Configuration');
    console.log('Environment:', envConfig.envName);
    console.log('Mode:', envConfig.env);
    console.log('Server Port:', envConfig.server.port);
    console.log('API Timeout:', envConfig.api.timeout);
    console.groupEnd();
  }
}

/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(
  feature: keyof EnvironmentConfig['features']
): boolean {
  return envConfig.features[feature];
}

/**
 * Get environment-specific API timeout
 */
export function getApiTimeout(): number {
  return envConfig.api.timeout;
}

/**
 * Get environment-specific server configuration
 */
export function getServerConfig(): EnvironmentConfig['server'] {
  return envConfig.server;
}

/**
 * Get Supabase configuration
 */
export function getSupabaseConfig(): EnvironmentConfig['supabase'] {
  return envConfig.supabase;
}

/**
 * Get External API configuration
 */
export function getExternalApiConfig(): EnvironmentConfig['externalApi'] {
  return envConfig.externalApi;
}

/**
 * Get Audio Upload configuration
 */
export function getAudioUploadConfig(): EnvironmentConfig['audioUpload'] {
  return envConfig.audioUpload;
}

/**
 * Get Section Upload configuration
 */
export function getSectionUploadConfig(): EnvironmentConfig['sectionUpload'] {
  return envConfig.sectionUpload;
}

/**
 * Get Gateway API configuration
 */
export function getGatewayConfig(): EnvironmentConfig['gateway'] {
  return envConfig.gateway;
}

export default envConfig;
